import {useAui} from '@assistant-ui/react';
import {IconButton, toast} from '@wandb/components';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {publishExternalWBAgentTurnCreated} from '../../agentChat/crossTabSync';
import {
  cloneThread,
  type CloneThreadResult,
} from '../../agentChat/mutations/cloneThread';
import {useActiveThread} from '../../agentChat/store';
import type {Thread} from '../../agentChat/store/thread';
import {useAgentChatReadonly} from '../../agentChat/useAgentChatReadonly';
import {internalTooltip, useIsInternalUser} from './internalTools';

/**
 * forkSourceTurnId returns the newest terminal backend turn, if the active
 * thread is safe to clone. The message tree can end on an older node when the
 * latest errored turn produced no renderable messages, so latestTurnId is the
 * authoritative source for this current-chat V1 action.
 */
export function forkSourceTurnId(thread: Thread | undefined): string | null {
  if (
    thread == null ||
    thread.loadStatus !== 'loaded' ||
    thread.status === 'running'
  ) {
    return null;
  }

  return thread.latestTurnId;
}

/**
 * WBAgentForkButton is the internal-only entry point for server-side chat
 * forking. The returned thread is opened through AUI's ordinary history
 * switch, so hydration and future continuation use the existing code path.
 */
export const WBAgentForkButton = () => {
  const aui = useAui();
  const client = useWbAgentClient();
  const activeThread = useActiveThread();
  const isInternal = useIsInternalUser();
  const isReadonly = useAgentChatReadonly();
  const [isForking, setIsForking] = useState(false);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const sourceTurnId = forkSourceTurnId(activeThread);
  const sourceThreadId =
    sourceTurnId == null ? null : (activeThread?.id ?? null);
  // Keep the current source fresh during render. A pending response can settle
  // after a conversation switch commits but before passive effects clear the
  // old source.
  const latestSourceRef = useRef<{
    sourceThreadId: string;
    sourceTurnId: string;
  } | null>(null);
  latestSourceRef.current =
    sourceThreadId == null || sourceTurnId == null
      ? null
      : {sourceThreadId, sourceTurnId};
  const isForkingDisabled =
    client == null || sourceTurnId == null || isReadonly || isForking;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Do not abort the POST: the server may already have committed the clone,
      // so mountedRef only prevents stale UI/navigation work.
      mountedRef.current = false;
    };
  }, []);

  const handleFork = useCallback(async () => {
    if (
      client == null ||
      sourceThreadId == null ||
      sourceTurnId == null ||
      isReadonly ||
      inFlightRef.current
    ) {
      return;
    }

    const requestSource = {sourceThreadId, sourceTurnId};
    inFlightRef.current = true;
    setIsForking(true);

    try {
      let result: CloneThreadResult;
      try {
        result = await cloneThread({
          client,
          sourceTurnId,
        });
      } catch {
        if (mountedRef.current) {
          toast.error('Failed to fork chat.');
        }
        return;
      }

      const latestSource = latestSourceRef.current;
      const shouldNavigate =
        mountedRef.current &&
        latestSource?.sourceThreadId === requestSource.sourceThreadId &&
        latestSource.sourceTurnId === requestSource.sourceTurnId;
      if (!shouldNavigate) {
        publishExternalWBAgentTurnCreated({
          type: 'turn-created',
          threadId: result.thread_id,
          turnId: result.turn_id,
        });
        return;
      }

      // Switching through the existing adapter fetches and hydrates the clone
      // exactly like selecting a normal sidebar row. Wait for that handoff so
      // the action stays visibly pending until the destination is ready.
      try {
        await aui.threads().switchToThread(result.thread_id);
      } catch {
        // The clone is already committed. Its history invalidation below makes
        // it recoverable from the sidebar even if local navigation fails.
        toast.error("Forked chat, but couldn't open it.");
      }

      // The fork did not flow through the live turn runtime, so explicitly
      // invalidate this tab's history list and notify sibling tabs.
      publishExternalWBAgentTurnCreated({
        type: 'turn-created',
        threadId: result.thread_id,
        turnId: result.turn_id,
      });
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setIsForking(false);
      }
    }
  }, [aui, client, isReadonly, sourceThreadId, sourceTurnId]);

  return isInternal ? (
    <IconButton
      variant="ghost"
      icon="branch-commit"
      isPending={isForking}
      isDisabled={isForkingDisabled}
      aria-label="Fork chat"
      onClick={() => void handleFork()}
      tooltip={{
        children: internalTooltip(isForking ? 'Forking chat…' : 'Fork chat'),
      }}
    />
  ) : null;
};
